import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users,
  UserPlus,
  Coins,
  Trash2,
  Copy,
  Check,
  AlertCircle,
  RefreshCw,
  Upload,
  Download,
  FileSpreadsheet,
  Building2,
  Mail,
  Phone,
  Briefcase,
  User,
  CreditCard,
  Globe
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrganizationAgents() {
  const { user, token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({
    total_agents: 0,
    credits: 0,
    distributed_credits: 0
  });
  
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [showAllocateCredits, setShowAllocateCredits] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [createdAgentPassword, setCreatedAgentPassword] = useState(null);
  const [createdAgentEmail, setCreatedAgentEmail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Form states
  const [newAgent, setNewAgent] = useState({ 
    business_name: '', 
    representative_name: '', 
    email: '', 
    phone: '', 
    country: '',
    quota: 0
  });
  const [allocateCredits, setAllocateCredits] = useState({ credits: 0, message: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // CSV import states
  const [csvFile, setCsvFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/organization/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data.agents || []);
      setStats({
        total_agents: response.data.total_agents || 0,
        credits: response.data.credits || 0,
        distributed_credits: response.data.distributed_credits || 0
      });
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load agents data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAgents();
    }
  }, [token]);

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!newAgent.business_name || !newAgent.email || !newAgent.country) {
      toast.error('Please fill in all required fields (including country)');
      return;
    }
    
    try {
      setSubmitting(true);
      // Map to backend expected format
      const payload = {
        name: newAgent.business_name,
        representative_name: newAgent.representative_name,
        email: newAgent.email,
        phone: newAgent.phone,
        country: newAgent.country,
        quota: newAgent.quota || 0
      };
      
      const response = await axios.post(`${API}/api/organization/agents`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if this was a new agent or existing agent linked
      if (response.data.status === 'existing_agent_linked') {
        toast.success(`Existing agent linked! ${response.data.quota} credits allocated.`);
        // No password to show for existing agents
        setCreatedAgentPassword(null);
        setCreatedAgentEmail(null);
        setShowCreateAgent(false);
        setNewAgent({ business_name: '', representative_name: '', email: '', phone: '', country: '', quota: 0 });
      } else {
        toast.success('New agent created successfully!');
        setCreatedAgentPassword(response.data.temp_password);
        setCreatedAgentEmail(newAgent.email);
      }
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAllocateCredits = async (e) => {
    e.preventDefault();
    if (allocateCredits.credits <= 0) {
      toast.error('Please enter a valid credit amount');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/api/organization/agents/${selectedAgent.id}/allocate-credits`,
        {
          agent_id: selectedAgent.id,
          credits: allocateCredits.credits,
          message: allocateCredits.message || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowAllocateCredits(false);
      setAllocateCredits({ credits: 0, message: '' });
      setSelectedAgent(null);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate credits');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAgent = async (agent) => {
    const confirmMsg = `Remove "${agent.name}" from your organization?\n\n` +
      `• They can still login as an independent agent\n` +
      `• Allocated credits will be returned to you\n\n` +
      `Click OK to remove, Cancel to keep.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    // Ask if they want to permanently delete
    const permanentDelete = window.confirm(
      `Do you also want to PERMANENTLY DELETE this user?\n\n` +
      `• Click OK to delete forever (can re-create with same email later)\n` +
      `• Click Cancel to just remove from organization (user can still login)`
    );
    
    try {
      const response = await axios.delete(
        `${API}/api/organization/agents/${agent.id}?permanent=${permanentDelete}`, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(response.data.message);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove agent');
    }
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await axios.post(`${API}/api/organization/agents/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data.results);
      toast.success(response.data.message);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "business_name,representative_name,email,phone,quota\nABC Consultancy,John Doe,john@abc.com,+1234567890,10\nXYZ Agency,Jane Smith,jane@xyz.com,+0987654321,20";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agent_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openAllocateDialog = (agent) => {
    setSelectedAgent(agent);
    setAllocateCredits({ credits: 0, message: '' });
    setShowAllocateCredits(true);
  };

  const resetCreateDialog = () => {
    setShowCreateAgent(false);
    setCreatedAgentPassword(null);
    setCreatedAgentEmail(null);
    setNewAgent({ business_name: '', representative_name: '', email: '', phone: '', quota: 0 });
  };

  const resetImportDialog = () => {
    setShowImportCSV(false);
    setCsvFile(null);
    setImportResults(null);
  };

  // All agents are shown (no regional office filter)
  const filteredAgents = agents;

  if (loading) {
    return (
      <DashboardLayout role="organization">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="organization">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Agents</h2>
            <p className="text-muted-foreground">Manage your agents network</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline"
              onClick={() => setShowImportCSV(true)}
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button 
              className="bg-lime-500 hover:bg-lime-600 text-white"
              onClick={() => {
                resetCreateDialog();
                setShowCreateAgent(true);
              }}
              data-testid="add-agent-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Agent
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Agents</p>
                  <p className="text-2xl font-heading font-bold text-blue-500">
                    {stats.total_agents}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Distributed Credits</p>
                  <p className="text-2xl font-heading font-bold text-amber-500">{stats.distributed_credits}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Available Credits</p>
                  <p className="text-2xl font-heading font-bold text-lime-500">{stats.credits}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agents Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div>
                <CardTitle className="font-heading">All Agents</CardTitle>
                <CardDescription>View and manage your agents network</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchAgents}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredAgents.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business Name</TableHead>
                    <TableHead>Representative</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Quota</TableHead>
                    <TableHead>Allocated</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgents.map((agent) => (
                    <TableRow key={agent.id} data-testid={`agent-row-${agent.id}`}>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell className="text-muted-foreground">{agent.representative_name || '-'}</TableCell>
                      <TableCell>{agent.email}</TableCell>
                      <TableCell>{agent.phone || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          <Briefcase className="w-3 h-3 mr-1" />
                          Agent
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{agent.quota || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                          {agent.allocated_credits}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAllocateDialog(agent)}
                            disabled={stats.credits === 0}
                          >
                            <Coins className="w-4 h-4 mr-1" />
                            Allocate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleRemoveAgent(agent)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No agents yet</p>
                <p className="text-sm mt-2">
                  Create agents to help manage interviews.
                </p>
                <div className="flex gap-2 justify-center mt-4">
                  <Button onClick={() => setShowImportCSV(true)} variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                  <Button onClick={() => { resetCreateDialog(); setShowCreateAgent(true); }}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Manually
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateAgent} onOpenChange={setShowCreateAgent}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Agent</DialogTitle>
            <DialogDescription>
              Add an agent to your organization.
            </DialogDescription>
          </DialogHeader>
          
          {createdAgentPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-400">
                    Agent Created!
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Share these credentials (also sent via email):
                </p>
                <div className="bg-white dark:bg-gray-800 rounded border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="font-mono text-sm">{createdAgentEmail}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Password:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{createdAgentPassword}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(createdAgentPassword)}
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <Badge>Agent</Badge>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={resetCreateDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreateAgent} className="space-y-4">
              {/* Business Name */}
              <div className="space-y-2">
                <Label htmlFor="business-name" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Business Name *
                </Label>
                <Input
                  id="business-name"
                  placeholder="ABC Consultancy Pvt Ltd"
                  value={newAgent.business_name}
                  onChange={(e) => setNewAgent({ ...newAgent, business_name: e.target.value })}
                  required
                  data-testid="business-name-input"
                />
              </div>

              {/* Representative Name */}
              <div className="space-y-2">
                <Label htmlFor="representative-name" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Representative Name
                </Label>
                <Input
                  id="representative-name"
                  placeholder="John Doe"
                  value={newAgent.representative_name}
                  onChange={(e) => setNewAgent({ ...newAgent, representative_name: e.target.value })}
                  data-testid="representative-name-input"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="agent-email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address *
                </Label>
                <Input
                  id="agent-email"
                  type="email"
                  placeholder="contact@abcconsultancy.com"
                  value={newAgent.email}
                  onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                  required
                  data-testid="agent-email-input"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="agent-phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  id="agent-phone"
                  placeholder="+1 234 567 8900"
                  value={newAgent.phone}
                  onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })}
                  data-testid="agent-phone-input"
                />
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label htmlFor="agent-country" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Country *
                </Label>
                <select
                  id="agent-country"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={newAgent.country}
                  onChange={(e) => setNewAgent({ ...newAgent, country: e.target.value })}
                  required
                  data-testid="agent-country-input"
                >
                  <option value="">Select Country</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="United States">United States</option>
                  <option value="India">India</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="Bangladesh">Bangladesh</option>
                  <option value="Nigeria">Nigeria</option>
                  <option value="Ghana">Ghana</option>
                  <option value="Kenya">Kenya</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Canada">Canada</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Italy">Italy</option>
                  <option value="Spain">Spain</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="China">China</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="Malaysia">Malaysia</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Philippines">Philippines</option>
                  <option value="Indonesia">Indonesia</option>
                  <option value="Thailand">Thailand</option>
                  <option value="Vietnam">Vietnam</option>
                  <option value="Nepal">Nepal</option>
                  <option value="Sri Lanka">Sri Lanka</option>
                  <option value="South Africa">South Africa</option>
                  <option value="Egypt">Egypt</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Quota */}
              <div className="space-y-2">
                <Label htmlFor="agent-quota" className="flex items-center gap-2">
                  <Coins className="w-4 h-4" />
                  Initial Credit Quota
                </Label>
                <Input
                  id="agent-quota"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={newAgent.quota || ''}
                  onChange={(e) => setNewAgent({ ...newAgent, quota: parseInt(e.target.value) || 0 })}
                  data-testid="agent-quota-input"
                />
                <p className="text-xs text-muted-foreground">
                  Credits to allocate immediately from your balance (optional)
                </p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetCreateDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-lime-500 hover:bg-lime-600">
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Agent
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Allocate Credits Dialog */}
      <Dialog open={showAllocateCredits} onOpenChange={setShowAllocateCredits}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate Credits</DialogTitle>
            <DialogDescription>
              Allocate credits to {selectedAgent?.name}. 
              These credits can be used by the agent for interviews or distributed to their partner agents.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAllocateCredits} className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Your Available Credits:</span>
                <span className="font-medium text-lime-500">{stats.credits}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Their Current Allocated:</span>
                <span className="font-medium">{selectedAgent?.allocated_credits || 0}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="credits-amount">Credits to Allocate *</Label>
              <Input
                id="credits-amount"
                type="number"
                min="1"
                max={stats.credits}
                placeholder="Enter amount"
                value={allocateCredits.credits || ''}
                onChange={(e) => setAllocateCredits({ ...allocateCredits, credits: parseInt(e.target.value) || 0 })}
                required
              />
              <div className="flex gap-2 mt-2">
                {[1, 5, 10, 25, 50].map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={amount > stats.credits}
                    onClick={() => setAllocateCredits({ ...allocateCredits, credits: amount })}
                  >
                    +{amount}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="credits-message">Message (Optional)</Label>
              <Textarea
                id="credits-message"
                placeholder="Add a note..."
                value={allocateCredits.message}
                onChange={(e) => setAllocateCredits({ ...allocateCredits, message: e.target.value })}
                rows={2}
              />
            </div>

            {allocateCredits.credits > 0 && (
              <div className="bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-lg p-3">
                <p className="text-sm">
                  After allocation: <strong className="text-lime-600">{stats.credits - allocateCredits.credits}</strong> credits remaining
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAllocateCredits(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || allocateCredits.credits <= 0 || allocateCredits.credits > stats.credits}
                className="bg-lime-500 hover:bg-lime-600"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Allocating...
                  </>
                ) : (
                  <>
                    <Coins className="w-4 h-4 mr-2" />
                    Allocate {allocateCredits.credits} Credits
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showImportCSV} onOpenChange={setShowImportCSV}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-lime-500" />
              Bulk Import Agents
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to add agents to your network. Importing agents is <strong className="text-lime-600">free</strong> - no credits required. 
              The "quota" column is optional for allocating credits to agents.
            </DialogDescription>
          </DialogHeader>
          
          {importResults ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-400">
                    Import Complete!
                  </span>
                </div>
                <p className="text-sm space-x-2">
                  {(importResults.created?.length > 0) && (
                    <span className="text-green-600">{importResults.created.length} new agents created</span>
                  )}
                  {(importResults.linked?.length > 0) && (
                    <span className="text-blue-600">{importResults.linked.length} existing agents linked</span>
                  )}
                  {importResults.failed?.length > 0 && (
                    <span className="text-red-600">({importResults.failed.length} failed)</span>
                  )}
                </p>
                {importResults.total_credits_allocated > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Total credits allocated: {importResults.total_credits_allocated}
                  </p>
                )}
              </div>
              
              {/* New Agents Created */}
              {importResults.created?.length > 0 && (
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2">
                  <p className="text-xs font-medium mb-2 text-green-600">✓ New Agents Created:</p>
                  {importResults.created.map((agent, idx) => (
                    <div key={idx} className="text-xs p-1.5 bg-green-50 dark:bg-green-900/20 rounded mb-1 flex justify-between">
                      <span>{agent.name} ({agent.email})</span>
                      <code className="bg-background px-1 rounded text-green-700">{agent.temp_password}</code>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Existing Agents Linked */}
              {importResults.linked?.length > 0 && (
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2">
                  <p className="text-xs font-medium mb-2 text-blue-600">↗ Existing Agents Linked:</p>
                  {importResults.linked.map((agent, idx) => (
                    <div key={idx} className="text-xs p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded mb-1 flex justify-between">
                      <span>{agent.name} ({agent.email})</span>
                      <span className="text-blue-600">+{agent.quota} credits</span>
                    </div>
                  ))}
                </div>
              )}
              
              {importResults.failed?.length > 0 && (
                <div className="max-h-24 overflow-y-auto">
                  <p className="text-xs font-medium mb-1 text-red-600">✗ Failed:</p>
                  {importResults.failed.map((item, idx) => (
                    <div key={idx} className="text-xs text-red-600 p-1.5 bg-red-50 rounded mb-1">
                      {item.email}: {item.error}
                    </div>
                  ))}
                </div>
              )}
              
              <Button onClick={resetImportDialog} className="w-full">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Upload Area */}
              <div 
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-lime-500 hover:bg-lime-50/50 dark:hover:bg-lime-900/10 transition-all"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="hidden"
                />
                {csvFile ? (
                  <>
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-lime-500 mb-2" />
                    <p className="font-medium">{csvFile.name}</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2"
                      onClick={(e) => { e.stopPropagation(); setCsvFile(null); }}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Click to select CSV file</p>
                  </>
                )}
              </div>
              
              {/* CSV Format Info */}
              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs font-medium mb-1">Required CSV columns:</p>
                <p className="text-xs text-muted-foreground font-mono">
                  business_name, email, quota
                </p>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 mt-1 text-xs"
                  onClick={downloadTemplate}
                >
                  <Download className="w-3 h-3 mr-1" />
                  Download Template
                </Button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetImportDialog} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCSVImport} 
                  disabled={!csvFile || importing} 
                  className="flex-1 bg-lime-500 hover:bg-lime-600"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
